import {
	AppButton,
	Divider,
	DotMatrix,
	SegmentedTab,
	SegmentedTabList,
	SegmentedTabs
} from '@clickguard/design-system'
import moment from 'moment'
import CheckCircleBroken from '@untitled-ui/icons-react/build/esm/CheckCircleBroken'
import Tag02 from '@untitled-ui/icons-react/build/esm/Tag02'
import XClose from '@untitled-ui/icons-react/build/esm/XClose'
import { HiCheck } from 'react-icons/hi'
import { GInput } from '../../../../../../components/basic-blocks'
import { BillingFrequency, PaymentOverviewProps } from './types'
import { ChatService } from 'services/chat'
import { TermsNote } from './terms-note'
import { NewSubscriptionBenefits } from 'components/black-friday-banner/new-subscription-benefits'

/** $50k, $300k, $1.5M: a spend in thousands of dollars, as people say it. */
const BILLING_OPTIONS: { value: BillingFrequency; label: string }[] = [
	{ value: 'monthly', label: 'Monthly' },
	{ value: 'quarterly', label: 'Quarterly' },
	{ value: 'annually', label: 'Annually' }
]

export const PaymentOverview = ({
	planName,
	adSpendLimit,
	monthlyPrice,
	quarterlyPrice,
	annualPrice,
	totalPrice,
	billingFrequency,
	onBillingFrequencyChange,
	onConfirm,
	onContinueToPayment,
	couponCode,
	onCouponCodeChange,
	onApplyCoupon,
	hasTrial,
	adSpend,
	isCustomPlan,
	customSpendValue,
	onCustomSpendChange,
	appliedCoupon,
	couponLoading,
	couponError,
	onRemoveCoupon,
	paymentError,
	canSubmit,
	submitLabel,
	showSpinner,
	selectedPmType,
	renderCard = true
}: PaymentOverviewProps) => {
	const pricePerMonth =
		billingFrequency === 'annually'
			? annualPrice
			: billingFrequency === 'quarterly'
			? quarterlyPrice
			: monthlyPrice
	// The quote flow refuses less than $100,000 (plan-payment-page.tsx); say so
	// at the field instead of only in a toast on submit.
	const customSpendTooLow = !!customSpendValue && customSpendValue < 100000
	const trialEnd = moment().add(7, 'days')
	const chargeDate = trialEnd.format('MMM D, YYYY')
	// Total due — use actual API price to avoid rounding errors
	const totalDue = totalPrice
	const billingLabel =
		billingFrequency === 'annually'
			? 'billed annually'
			: billingFrequency === 'quarterly'
			? 'billed quarterly'
			: 'billed monthly'

	// Discount calculations. Shown as one total for the billing period, never
	// per month, so nobody has to multiply it out.
	const discountedTotal = appliedCoupon
		? appliedCoupon.percentOff
			? totalDue * (1 - appliedCoupon.percentOff / 100)
			: Math.max(0, totalDue - (appliedCoupon.amountOff ?? 0) / 100)
		: totalDue
	const discountTotal = totalDue - discountedTotal
	const money = (value: number) =>
		value.toLocaleString(undefined, {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		})

	return (
		<div className="lg:w-[398px]">
			<div
				className={`bg-white p-[16px] ${
					renderCard
						? 'border border-t-checkbox rounded-[20px] drop-shadow-[0px_1px_1px_rgba(16,24,40,0.05)] '
						: ''
				}`}
			>
				<div className="flex flex-col gap-[16px] w-full">
					{/* Header: title + billing toggle */}
					<div className="flex flex-col gap-[6px]">
						{/* Plan Overview Heading */}
						<h3 className="font-grotesk font-medium text-[18px] leading-[24px] text-t-base-blue text-center">
							Plan overview
						</h3>

						{/* Billing Toggle - pill segmented control */}
						<div className="flex flex-col items-center">
							{/* mb: room for the save badges that hang below the tabs. */}
							<div className="relative w-[252px] mb-[12px]">
								{/* DS SegmentedTabs, brand tone: the billing-cycle switch pattern. The
								    hand-rolled pills overflowed their fixed 226px track. Equal-width
								    tabs keep the save badges below centred under their labels. */}
								<SegmentedTabs
									value={billingFrequency}
									onChange={(v) =>
										onBillingFrequencyChange(v as BillingFrequency)
									}
									size="small"
									activeTone="brand"
									aria-label="Billing frequency"
								>
									<SegmentedTabList className="w-full">
										{BILLING_OPTIONS.map((option) => (
											<SegmentedTab
												key={option.value}
												value={option.value}
												label={option.label}
												className="flex-1 justify-center"
											/>
										))}
									</SegmentedTabList>
								</SegmentedTabs>
								{/* Save badges positioned below the toggle */}
								<div className="flex absolute top-[calc(100%-6px)] left-0 right-0 pointer-events-none">
									<div className="flex-1" />
									<div className="flex-1 flex justify-center">
										<span className="bg-b-yellow-tint-2 border border-b-yellow-tint-border text-t-default text-[9px] font-grotesk font-medium px-2 rounded-full leading-[20px] whitespace-nowrap">
											save 10%
										</span>
									</div>
									<div className="flex-1 flex justify-center">
										<span className="bg-b-yellow-tint-2 border border-b-yellow-tint-border text-t-default text-[9px] font-grotesk font-medium px-2 rounded-full leading-[20px] whitespace-nowrap">
											save 20%
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Plan Name, Price & Coupon section */}
					<div className="flex flex-col gap-[12px]">
						{/* Plan pricing display */}
						{isCustomPlan ? (
							<div className="flex flex-col gap-[12px]">
								<p className="font-grotesk font-medium text-[18px] leading-[28px] text-t-coupon text-center">
									Custom plan
								</p>
								<div className="bg-bg-plan-fill rounded-[8px] px-[12px] py-[12px] flex flex-col gap-[6px] border border-transparent focus-within:border-[rgb(var(--cg-brand))]">
									<p className="font-grotesk text-[12px] leading-[16px] text-t-gray-hold overflow-hidden text-ellipsis">
										Monthly ad spend (USD)
									</p>
									<div className="flex items-center gap-[4px]">
										<span className="font-grotesk text-[14px] leading-[18px] text-t-desc-gray">
											$
										</span>
										<input
											type="text"
											value={
												customSpendValue
													? customSpendValue.toLocaleString()
													: ''
											}
											onChange={(e) => {
												const raw = parseInt(
													e.target.value.replace(/\D/g, ''),
													10
												)
												onCustomSpendChange?.(isNaN(raw) ? 0 : raw)
											}}
											placeholder="300,000"
											inputMode="numeric"
											aria-label="Monthly ad spend (USD)"
											aria-invalid={customSpendTooLow}
											className="font-grotesk text-[20px] leading-[26px] text-t-coupon placeholder:text-t-gray-hold bg-transparent border-none outline-none w-full"
										/>
									</div>
								</div>
								{customSpendTooLow && (
									<p className="font-grotesk text-[12px] leading-[16px] text-red-500 -mt-[6px]">
										Enter $100,000 or more for a Custom plan.
									</p>
								)}
								<p className="font-grotesk text-[14px] leading-[16px] text-brand-tint-text text-center">
									Our team will send you an offer shortly
								</p>
							</div>
						) : (
							<div className="flex flex-col items-center gap-[2px]">
								<p className="font-grotesk font-medium text-[14px] leading-[18px] text-[rgb(var(--cg-text-secondary))] text-center">
									{planName} plan
								</p>
								<p className="font-grotesk font-bold text-[24px] leading-[28px] text-brand-tint-text tabular-nums">
									${Math.round(pricePerMonth)} / month
								</p>
								<p className="font-grotesk text-[14px] leading-[18px] text-t-gray-hold text-center">
									$
									{totalDue.toLocaleString(undefined, {
										minimumFractionDigits: 0,
										maximumFractionDigits: 0
									})}{' '}
									{billingLabel}
								</p>
							</div>
						)}

						<Divider />

						{/* Coupon. The applied state is the DS ChangePlanModal coupon chip
						    (Tag02 · code · XClose, then CheckCircleBroken + confirmation),
						    on the same DS tokens. The DS does not export it on its own;
						    move it into the DS when it gets a standalone component. */}
						{appliedCoupon && adSpendLimit ? null : appliedCoupon ? (
							<div className="flex flex-wrap items-center justify-between gap-[12px]">
								<span className="inline-flex items-center gap-[6px] rounded-lg border border-[rgb(var(--cg-brand-subtle))] bg-[rgb(var(--cg-selected)/0.5)] px-[8px] py-[6px]">
									<Tag02
										className="w-4 h-4 shrink-0 text-[rgb(var(--cg-icon-brand))]"
										aria-hidden="true"
									/>
									<span className="font-grotesk text-[14px] leading-[18px] font-medium text-[rgb(var(--cg-brand))]">
										{appliedCoupon.code}
									</span>
									{!appliedCoupon.locked && (
										<button
											type="button"
											onClick={onRemoveCoupon}
											aria-label="Remove coupon"
											className="inline-flex cursor-pointer items-center rounded border-0 bg-transparent p-0 text-[rgb(var(--cg-brand))] focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_rgb(var(--cg-border-focus)/0.75)]"
										>
											<XClose className="w-3 h-3" aria-hidden="true" />
										</button>
									)}
								</span>
								<span className="flex items-center gap-[6px] font-grotesk text-[14px] leading-[18px] text-[rgb(var(--cg-text-secondary))]">
									<CheckCircleBroken
										className="w-4 h-4 shrink-0"
										aria-hidden="true"
									/>
									{appliedCoupon.appliedText ?? 'Coupon applied'}
								</span>
							</div>
						) : (
							<div className="flex gap-[12px] items-center">
								<div className="w-[300px]">
									<GInput
										placeholder="Coupon code"
										value={couponCode}
										disabled={couponLoading}
										onChange={(e) => onCouponCodeChange(e.target.value)}
										inputClassName="!border-t-gray-input rounded-lg !h-[38px] text-[14px] font-grotesk"
									/>
								</div>
								<button
									type="button"
									onClick={onApplyCoupon}
									disabled={!couponCode.trim() || couponLoading}
									className={`shrink-0 font-grotesk text-t-email-blue font-medium text-[14px] leading-[18px] transition-colors ${
										!couponCode.trim() || couponLoading
											? ' cursor-not-allowed'
											: ' cursor-pointer'
									}`}
								>
									{couponLoading ? '...' : 'Apply'}
								</button>
							</div>
						)}
						{couponError && (
							<p className="font-grotesk text-[11px] text-red-500 -mt-4">
								{couponError}
							</p>
						)}
					</div>

					{adSpendLimit && (
						<NewSubscriptionBenefits
							{...adSpendLimit}
							isCustomPlan={isCustomPlan}
							couponCode={appliedCoupon?.code}
						/>
					)}

					{/* Pricing Summary + CTA */}
					<div className="flex flex-col gap-[12px]">
						{/* Billing totals. */}
						<div className="flex flex-col gap-[8px] font-grotesk text-[14px] leading-[20px] tabular-nums">
							{!isCustomPlan && (
								<>
									{appliedCoupon && (
										<div className="flex justify-between items-center gap-[12px]">
											<span className="text-[rgb(var(--cg-text-secondary))]">
												{appliedCoupon.label ?? 'Discount'}
											</span>
											<span className="font-medium text-[#17B26A] whitespace-nowrap">
												-${money(discountTotal)}
											</span>
										</div>
									)}

									<div className="flex justify-between items-center gap-[12px]">
										<span className="text-[rgb(var(--cg-text-secondary))]">
											Charged {chargeDate}
										</span>
										<span className="font-medium text-[#7169FF] whitespace-nowrap">
											${money(appliedCoupon ? discountedTotal : totalDue)}
										</span>
									</div>
								</>
							)}
							<Divider />
							<div className="flex justify-between items-center gap-[12px] text-[16px] leading-[22px] font-semibold text-t-base-blue">
								<span>Due today</span>
								<span className="text-brand-tint-text">
									{hasTrial || isCustomPlan ? '$0.00' : `$${pricePerMonth}`}
								</span>
							</div>
							<p className="text-[11px] leading-[15px] text-t-gray-hold">
								No charge until your trial ends. USD, excluding taxes.
							</p>
						</div>

						{/* CTA Button */}
						<div className="flex flex-col gap-[6px]">
							{/* Mobile: scroll to payment section */}
							<div className="lg:hidden">
								<AppButton
									variant="marketing"
									size="lg"
									fullWidth
									label="Continue to payment"
									onClick={onContinueToPayment ?? onConfirm}
								/>
							</div>
							{/* Desktop: confirm and start trial. The terms line sits right
							    under the action it covers; on mobile it sits under the
							    page-bottom CTA instead (plan-payment-page.tsx). */}
							<div className="hidden lg:flex flex-col gap-[8px]">
								<AppButton
									variant="marketing"
									size="md"
									fullWidth
									label={submitLabel}
									leftIcon={
										showSpinner ? (
											<DotMatrix size="xs" tone="inherit" />
										) : undefined
									}
									disabled={!canSubmit}
									onClick={onConfirm}
								/>
								<TermsNote />
							</div>
						</div>
						{paymentError && (
							<p className="font-grotesk text-[12px] leading-[18px] text-[#F04438] text-center">
								This payment method was not accepted by the processor. Reason:{' '}
								<span className="italic">{paymentError}</span>.<br />
								<br />
								Please try again or{' '}
								<span
									className="underline cursor-pointer"
									onClick={() => ChatService.open()}
								>
									contact support
								</span>
							</p>
						)}
					</div>
				</div>
			</div>

			{/* Trust bullets below the card */}
			<div className="flex flex-col gap-[8px] px-[32px] mt-[16px]">
				<div className="flex items-start gap-[8px]">
					<HiCheck className="w-4 h-4 text-t-email-blue shrink-0 mt-[2px]" />
					<p className="font-grotesk text-[12px] leading-[16px] text-t-default">
						We&apos;ll remind you halfway through your trial and again the day
						before it ends — no surprises.
					</p>
				</div>
				<div className="flex items-start gap-[8px]">
					<HiCheck className="w-4 h-4 text-t-email-blue shrink-0 mt-[2px]" />
					<p className="font-grotesk text-[12px] leading-[16px] text-t-default">
						Switch plans or cancel at anytime.
					</p>
				</div>
				{/* Only relevant for cards. Other PM types (SEPA, BACS, etc.)
				    skip the $1 pre-auth on the BE, and Stripe's PaymentElement
				    renders the appropriate mandate disclosure inline for them.
				    Default-show before the user picks a tab (PaymentElement
				    defaults to card). */}
				{(!selectedPmType || selectedPmType === 'card') && (
					<div className="flex items-start gap-[8px]">
						<HiCheck className="w-4 h-4 text-t-email-blue shrink-0 mt-[2px]" />
						<p className="font-grotesk text-[12px] leading-[16px] text-t-default">
							A $1 authorization may be placed on your card to verify it. It
							won&apos;t be charged.
						</p>
					</div>
				)}
			</div>
		</div>
	)
}
